import { useState, useEffect, useCallback, useRef } from "react"
import { api } from "../utils/apiComponent/apiClient"
import { endpoints } from "../utils/endpoints"

/**
 * Hook centralisé pour vérifier l'accès aux ressources
 * Utilise les endpoints /verify-access qui retournent :
 * {
 *   hasAccess: boolean,
 *   reason?: string,
 *   code?: string,
 *   details?: object
 * }
 */
export function useAccessCheck(resourceType, resourceId) {
  const [accessData, setAccessData] = useState({
    hasAccess: false,
    isChecking: true,
    reason: null,
    code: null,
    details: null,
    error: null
  })

  // Utiliser un ref pour éviter les appels multiples
  const isCheckingRef = useRef(false)
  const lastResourceIdRef = useRef(null)
  const lastResourceTypeRef = useRef(null)
  const hasDataRef = useRef(false)

  const checkAccess = useCallback(async () => {
    // Éviter les appels multiples si déjà en cours
    if (isCheckingRef.current) {
      return
    }

    if (!resourceId) {
      setAccessData({
        hasAccess: false,
        isChecking: false,
        reason: "ID de ressource manquant",
        code: "RESOURCE_ID_MISSING",
        details: null,
        error: null
      })
      return
    }

    // Vérifier si les paramètres ont changé
    const resourceChanged = 
      lastResourceIdRef.current !== resourceId || 
      lastResourceTypeRef.current !== resourceType

    // Si les paramètres n'ont pas changé et qu'on a déjà des données, ne pas refaire l'appel
    if (!resourceChanged && hasDataRef.current) {
      return
    }

    // Marquer comme en cours de vérification
    isCheckingRef.current = true
    lastResourceIdRef.current = resourceId
    lastResourceTypeRef.current = resourceType
    hasDataRef.current = false

    setAccessData(prev => ({ ...prev, isChecking: true, error: null }))

    try {
      let endpoint
      switch (resourceType) {
        case 'course':
          endpoint = endpoints.verifyCourseAccess(resourceId)
          break
        case 'module':
          endpoint = endpoints.verifyModuleAccess(resourceId)
          break
        case 'lesson':
          endpoint = endpoints.verifyLessonAccess(resourceId)
          break
        case 'block':
          endpoint = endpoints.verifyBlockAccess(resourceId)
          break
        case 'quiz':
          endpoint = endpoints.verifyQuizAccess(resourceId)
          break
        default:
          throw new Error(`Type de ressource non supporté: ${resourceType}`)
      }

      const data = await api.get(endpoint)

      hasDataRef.current = true
      setAccessData({
        hasAccess: data.hasAccess === true,
        isChecking: false,
        reason: data.reason || null,
        code: data.code || null,
        details: data.details || null,
        error: null
      })
    } catch (error) {
      hasDataRef.current = true
      setAccessData({
        hasAccess: false,
        isChecking: false,
        reason: error.message || "Erreur lors de la vérification d'accès",
        code: error.status === 401 ? "USER_NOT_AUTHENTICATED" : "ERROR",
        details: null,
        error: error
      })
    } finally {
      isCheckingRef.current = false
    }
  }, [resourceType, resourceId])

  useEffect(() => {
    // Réinitialiser le flag si les paramètres changent
    if (lastResourceIdRef.current !== resourceId || lastResourceTypeRef.current !== resourceType) {
      hasDataRef.current = false
    }
    checkAccess()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, resourceId])

  return {
    ...accessData,
    refetch: checkAccess
  }
}

